"use client";

import { type ReactNode, useMemo, useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";

/* ------------------------------------------------------------------ */
/*  Regexes + word lists (module level per Biome rule)                */
/* ------------------------------------------------------------------ */

/* Lowercase letter at the start of the text, a line, or after . ! ?  */
const RE_SENTENCE_START = /(^|[.!?]["')\]]*\s+)([a-z])/gm;

/* The pronoun "i" written in lowercase (also catches i'm, i've, i'd) */
const RE_LOWERCASE_I = /\bi\b/g;

/* A period after one of these does not end a sentence ("et al. found").   */
/* "etc." is left out on purpose: it can end a sentence.                   */
const RE_ABBREVIATION_BEFORE =
  /(?:^|[\s([])(?:et al|e\.g|i\.e|cf|viz|vs|fig|figs|eq|eqs|dr|mr|mrs|ms|prof|st|jr|sr|inc|ltd|approx|ca|ibid|ed|eds|vol|pp?)\.$/i;
/* Line-start enumerators and abbreviations: "i) text", "a. text", "i.e. text" */
const RE_ENUMERATOR_FOLLOW = /^(?:[ivx]*\)|[ivx]*\.\s|\.[a-z])/i;
/* "i. speed": the period after a line-start list marker is not a full stop */
const RE_ENUMERATOR_BEFORE = /(?:^|\n)[ \t]*(?:[ivx]+|[a-z])\.$/i;
const RE_TEXT_BEFORE_ON_LINE = /[^\n]*$/;
const RE_NON_SPACE = /\S/;
const RE_ANY_LETTER = /[a-z]/i;
const RE_WHITESPACE_CHAR = /\s/;

/* Days, months, and common proper nouns — lowercase-only match.      */
/* "may", "march", and "august" are deliberately excluded: they are   */
/* everyday English words and would produce constant false positives. */
const PROPER_NOUNS: string[] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
  "january",
  "february",
  "april",
  "june",
  "july",
  "september",
  "october",
  "november",
  "december",
  "english",
  "spanish",
  "french",
  "german",
  "chinese",
  "american",
  "british",
  "european",
  "africa",
  "america",
  "asia",
  "australia",
  "canada",
  "england",
  "europe",
  "france",
  "germany",
  "india",
  "japan",
  "london",
  "mexico",
  "paris",
  "russia",
  "google",
  "microsoft",
  "facebook",
  "youtube",
  "wikipedia",
  "christmas",
  "easter",
  "halloween",
  "thanksgiving",
];

const RE_PROPER_NOUN = new RegExp(`\\b(?:${PROPER_NOUNS.join("|")})\\b`, "g");
const RE_SPLIT_WORDS = /\s+/;

/* ------------------------------------------------------------------ */
/*  Analysis                                                          */
/* ------------------------------------------------------------------ */

interface CapFinding {
  index: number;
  length: number;
  original: string;
  suggestion: string;
  rule: string;
}

interface AnalysisResult {
  wordCount: number;
  findings: CapFinding[];
  highlighted: ReactNode[];
}

function capitalize(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

function collectSentenceStartFindings(
  text: string,
  findings: CapFinding[],
  strings: Strings
): void {
  for (const match of text.matchAll(RE_SENTENCE_START)) {
    const letterIndex = match.index + match[1].length;
    const before = text.slice(0, letterIndex).trimEnd();
    if (
      match[1].length > 0 &&
      (RE_ABBREVIATION_BEFORE.test(before) || RE_ENUMERATOR_BEFORE.test(before))
    ) {
      continue;
    }
    if (RE_ENUMERATOR_FOLLOW.test(text.slice(letterIndex + 1))) {
      continue;
    }
    findings.push({
      index: letterIndex,
      length: 1,
      original: match[2],
      suggestion: match[2].toUpperCase(),
      rule: strings.sentenceStartRule,
    });
  }
}

/** "i.e.", "(i)", "i)" or "i." as a list marker, and "i-th": not the pronoun. */
function isNotPronounI(text: string, index: number): boolean {
  const after = text[index + 1] ?? "";
  const before = text[index - 1] ?? "";
  if (after === "." && RE_ANY_LETTER.test(text[index + 2] ?? "")) {
    return true;
  }
  if (before === "(" && after === ")") {
    return true;
  }
  if (before === "." || after === "-") {
    return true;
  }
  if (
    after === ")" ||
    (after === "." && RE_WHITESPACE_CHAR.test(text[index + 2] ?? " "))
  ) {
    // A list marker only when nothing else precedes it on the line.
    const line = RE_TEXT_BEFORE_ON_LINE.exec(text.slice(0, index))?.[0] ?? "";
    return !RE_NON_SPACE.test(line);
  }
  return false;
}

function collectLowercaseIFindings(
  text: string,
  findings: CapFinding[],
  strings: Strings
): void {
  for (const match of text.matchAll(RE_LOWERCASE_I)) {
    if (isNotPronounI(text, match.index)) {
      continue;
    }
    findings.push({
      index: match.index,
      length: 1,
      original: "i",
      suggestion: "I",
      rule: strings.pronounIRule,
    });
  }
}

function collectProperNounFindings(
  text: string,
  findings: CapFinding[],
  strings: Strings
): void {
  for (const match of text.matchAll(RE_PROPER_NOUN)) {
    findings.push({
      index: match.index,
      length: match[0].length,
      original: match[0],
      suggestion: capitalize(match[0]),
      rule: strings.properNounRule,
    });
  }
}

const COLLECTORS = [
  collectSentenceStartFindings,
  collectLowercaseIFindings,
  collectProperNounFindings,
];

/**
 * Four, and deliberately NOT `COLLECTORS.length`.
 *
 * The tool advertises four rule-based checks and the FAQ lists four: a lowercase
 * sentence start, the standalone pronoun "i", lowercase days and months, and a list of
 * about forty other proper nouns. Three functions implement them, because the last two
 * share one word list and one regex — an implementation detail, not a fourth check that
 * went missing.
 *
 * Deriving the number from the collector count therefore reads as tidier and is wrong:
 * it would publish "3 rules" against a FAQ listing four, in seventeen languages, and the
 * page would contradict itself. Splitting the word list in two would make the derivation
 * honest, at the cost of a new rule label needing sixteen translations — worth doing if
 * this file is opened for other reasons, not worth doing for a constant.
 *
 * It lives here rather than in the strings so the number has one home: both copies that
 * state it interpolate `{count}`, so no translation carries a numeral that can go stale.
 */
export const RULE_COUNT = 4;

export function collectFindings(text: string, strings: Strings): CapFinding[] {
  const findings: CapFinding[] = [];
  for (const collect of COLLECTORS) {
    collect(text, findings, strings);
  }

  findings.sort((a, b) => a.index - b.index);

  /* Deduplicate overlapping findings (e.g. a lowercase day at a      */
  /* sentence start) — keep the first at each position.               */
  const deduped: CapFinding[] = [];
  let lastEnd = -1;
  for (const finding of findings) {
    if (finding.index >= lastEnd) {
      deduped.push(finding);
      lastEnd = finding.index + finding.length;
    }
  }
  return deduped;
}

function buildHighlighted(text: string, findings: CapFinding[]): ReactNode[] {
  const nodes: ReactNode[] = [];
  let last = 0;
  for (const finding of findings) {
    if (finding.index > last) {
      nodes.push(text.slice(last, finding.index));
    }
    nodes.push(
      <mark
        className="rounded bg-yellow-200 px-0.5 dark:bg-yellow-900/60 dark:text-yellow-100"
        key={`cap-${finding.index}`}
      >
        {text.slice(finding.index, finding.index + finding.length)}
      </mark>
    );
    last = finding.index + finding.length;
  }
  if (last < text.length) {
    nodes.push(text.slice(last));
  }
  return nodes;
}

function analyzeCapitalization(
  text: string,
  strings: Strings
): AnalysisResult | null {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return null;
  }

  const findings = collectFindings(text, strings);

  return {
    wordCount: trimmed.split(RE_SPLIT_WORDS).filter(Boolean).length,
    findings,
    highlighted: buildHighlighted(text, findings),
  };
}

function applyFinding(text: string, finding: CapFinding): string {
  return (
    text.slice(0, finding.index) +
    finding.suggestion +
    text.slice(finding.index + finding.length)
  );
}

function applyAllFindings(text: string, findings: CapFinding[]): string {
  let result = text;
  const descending = findings.slice().sort((a, b) => b.index - a.index);
  for (const finding of descending) {
    result = applyFinding(result, finding);
  }
  return result;
}

/* ------------------------------------------------------------------ */
/*  Stat card                                                         */
/* ------------------------------------------------------------------ */

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Page Component                                                    */
/* ------------------------------------------------------------------ */

/** English strings. A translated content file overrides these through `ui`. */
export const CAPITALIZATION_CHECKER_STRINGS = {
  textToCheckCapitalizationIn: "Text to check capitalization in",
  pasteOrTypeYourText:
    "Paste or type your text here to check capitalization...",
  totalWords: "Total Words",
  issuesFound: "Issues Found",
  checksRun: "Checks Run",
  yourTextWithIssuesHighlighted: "Your Text with Issues Highlighted",
  suggestedFixes: "Suggested Fixes",
  fixAllCount: "Fix All ({count})",
  fix: "Fix",
  noCapitalizationIssuesDetected: "No capitalization issues detected!",
  yourTextPassedAllFour:
    "Your text passed all {count} rule-based checks. Remember the tool only knows a limited list of proper nouns.",
  sentenceStartRule: "Sentence should start with a capital letter",
  pronounIRule: "The pronoun I is always capitalized",
  properNounRule: "Days, months, and proper nouns are capitalized",
  ruleCount: "{count} rules",
} as const;

type Strings = typeof CAPITALIZATION_CHECKER_STRINGS;

export default function CapitalizationCheckerWidget() {
  const [text, setText] = useState("");

  const strings = useToolUi(CAPITALIZATION_CHECKER_STRINGS);

  const fmt = useToolNumber();

  const analysis = useMemo(
    () => analyzeCapitalization(text, strings),
    [text, strings]
  );

  function handleFix(finding: CapFinding) {
    setText(applyFinding(text, finding));
  }

  function handleFixAll() {
    if (analysis && analysis.findings.length > 0) {
      setText(applyAllFindings(text, analysis.findings));
    }
  }

  return (
    <>
      {/* Input */}
      <textarea
        aria-label={strings.textToCheckCapitalizationIn}
        className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        onChange={(e) => setText(e.target.value)}
        placeholder={strings.pasteOrTypeYourText}
        value={text}
      />
      {/* Stats */}
      {analysis && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard
            label={strings.totalWords}
            value={fmt(analysis.wordCount)}
          />
          <StatCard
            label={strings.issuesFound}
            value={fmt(analysis.findings.length)}
          />
          <StatCard
            label={strings.checksRun}
            value={t(strings.ruleCount, { count: RULE_COUNT })}
          />
        </div>
      )}
      {/* Highlighted text */}
      {analysis && analysis.findings.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.yourTextWithIssuesHighlighted}
          </h2>
          <div className="whitespace-pre-wrap rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed">
            {analysis.highlighted}
          </div>
        </section>
      )}
      {/* Findings + fixes */}
      {analysis && analysis.findings.length > 0 && (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-foreground text-lg">
              {strings.suggestedFixes}
            </h2>
            <button
              className="rounded-lg bg-primary px-4 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90"
              onClick={handleFixAll}
              type="button"
            >
              {strings.fixAllCount.replace(
                "{count}",
                String(analysis.findings.length)
              )}
            </button>
          </div>
          <div className="space-y-3">
            {analysis.findings.map((finding) => (
              <div
                className="flex items-center justify-between gap-4 rounded-lg border border-border bg-card p-4"
                key={`${finding.index}-${finding.original}`}
              >
                <div>
                  <p className="text-foreground text-sm leading-relaxed">
                    <span className="font-medium text-red-600 dark:text-red-400">
                      {finding.original}
                    </span>
                    <span className="text-muted-foreground"> → </span>
                    <span className="font-medium text-green-700 dark:text-green-400">
                      {finding.suggestion}
                    </span>
                  </p>
                  <p className="mt-1 text-muted-foreground text-xs">
                    {finding.rule}
                  </p>
                </div>
                <button
                  className="shrink-0 rounded-lg border border-border bg-card px-3 py-1.5 font-medium text-foreground text-xs transition-colors hover:bg-muted"
                  onClick={() => handleFix(finding)}
                  type="button"
                >
                  {strings.fix}
                </button>
              </div>
            ))}
          </div>
        </section>
      )}
      {/* None found */}
      {analysis && analysis.findings.length === 0 && (
        <div className="rounded-lg border border-border bg-card p-5 text-center">
          <p className="font-medium text-foreground">
            {strings.noCapitalizationIssuesDetected}
          </p>
          <p className="mt-1 text-muted-foreground text-sm">
            {t(strings.yourTextPassedAllFour, { count: RULE_COUNT })}
          </p>
        </div>
      )}
    </>
  );
}
